<template>
  <wgu-app-tpl>
    <!-- when in Iframe, links shall be opened in new tab - see https://stackoverflow.com/a/22808227 -->
    <base
      v-if="inIframe()"
      target="_parent"
    />

    <PoiInfo
    slot="wgu-before-content"
    :color="baseColor"
    >

      <template v-slot:poiinfo-after-subtitle="slotProps">
        <v-sparkline
        v-if="slotProps.featureProperties"
        :value="extractTimeLineValues(slotProps.featureProperties.series)"
        :color="baseColor"
        ></v-sparkline>
      </template>

    </PoiInfo>

  </wgu-app-tpl>
</template>

<script>
  import Vue from 'vue'
  import WguAppTemplate from './WguAppTemplate.vue';
  import PoiInfo from './components/PoiInfo';
  import { Mapable } from '../src/mixins/Mapable.js';
  import { OlStyleFactory } from '../src/factory/OlStyle.js'
  import ClusterSource from 'ol/source/Cluster'
  import Point from 'ol/geom/Point';
  import LineString from 'ol/geom/LineString';
  import VectorLayer from 'ol/layer/Vector'
  import VectorSource from 'ol/source/Vector'
  import MapInteractionUtil from '../src/util/MapInteraction';
  import StyleUtil from '../src/util/Style';

  export default {
    name: 'my-wgu-app',
    mixins: [Mapable],
    components: {
      'wgu-app-tpl': WguAppTemplate,
      PoiInfo
    },
    data () {
      return {
        baseColor: Vue.prototype.$appConfig.baseColor
      }
    },
    methods: {
      /**
       * Checks if application is inside iFrame
       * taken from https://stackoverflow.com/a/326076/
       */
      inIframe () {
        try {
          return window.self !== window.top;
        } catch (e) {
          return true;
        }
      },
      onMapBound () {
        this.applyCategorizedStyleToLayers();
        this.applyClusterStyleToLayers();
      },

      /**
       * Extract values for sparkline
       *
       * @param {String} seriesValue The value of the series property
       *
       * @returns {Array} The array of the values to display.
       */
      extractTimeLineValues (seriesValue) {
        if (!seriesValue) {
          console.warn('No station data available.')
          return;
        }
        try {
          // seriesValue comes as String and needs to be parsed
          const seriesJson = JSON.parse(seriesValue);

          // reshape to required format
          return seriesJson.map(
            item => {
              return item['val']
            }
          );
        } catch (error) {
          console.warn('Parsing station data failed.');
        }
      },

      /**
       * Applies the category style to all layers that
       * have this setting enabled.
       */
      applyCategorizedStyleToLayers () {
        // find layer with category style
        const layers = this.map.getLayers();
        layers.forEach(layer => {
          const styleConf = layer.get('styleConf');

          // check if layer has a category style
          const hasCategoryStyle = styleConf &&
                                   styleConf.categoryKey &&
                                   styleConf.categories;

          if (hasCategoryStyle) {
            const styleFunction = (feature, resolution) => {
              // the name of the attribute that holds the categories
              const categoryKey = styleConf.categoryKey;

              // the current category to evaluate e.g. 'shop' or 'pharmacy'
              const categoryValue = feature.get(categoryKey);

              // the provided style for the catgory
              const catStyle = styleConf.categories[categoryValue];

              // create the respective style with the style factory
              let olStyle = OlStyleFactory.getInstance(catStyle);

              // add label to point
              if (olStyle && styleConf.label && styleConf.label.attribute &&
                  styleConf.label.attribute !== '') {
                // we need to clone the style, otherwise the text-based points cannot be shown anymore
                const clonedStyle = olStyle.clone();
                let newLabelStyle = OlStyleFactory.getTextStyleFunction(clonedStyle, styleConf.label)
                return [olStyle, newLabelStyle(feature, resolution)];
              }

              return olStyle;
            };

            // overwrite existing default style
            layer.setStyle(styleFunction);
          }
        });
      },

      /**
       * Applies cluster styling to all layers that have activated this setting.
       */
      applyClusterStyleToLayers () {
        // TODO: permalink shows helper layer

        const layers = this.map.getLayers();
        layers.forEach(layer => {
          const clusterConf = layer.get('clusterRendering');
          if (!clusterConf) {
            return
          }
          const twinSource = new VectorSource();
          const twinLayer = new VectorLayer({
            name: 'twin layer',
            source: twinSource
          });

          // sync visibility of both layers
          layer.on('change:visible', () => {
            const visible = layer.getVisible();
            twinLayer.setVisible(visible);
          });

          // hide in layer list
          twinLayer.set('displayInLayerList', false);

          // set new layer id
          twinLayer.set('lid', layer.get('lid') + '-cluster-helper');

          // hover
          const hoverable = layer.get('hoverable');
          twinLayer.set('hoverable', hoverable);
          layer.set('hoverable', false);
          twinLayer.set('hoverAttribute', layer.get('hoverAttribute'))

          // poiInfo
          twinLayer.set('poiInfo', layer.get('poiInfo'))

          // callout line style
          const callOutStyleSettings = clusterConf.calloutStyle ||
          {
            strokeColor: 'rgb(136, 138, 133, 0.3)',
            strokeWidth: 1
          };
          const calloutStyle = OlStyleFactory.getInstance(callOutStyleSettings);
          calloutStyle.setGeometry((feature) => {
            if (!feature || !feature.getGeometry() || !feature.get('originalGeometry')) {
              return;
            }
            const movedCoordinate = feature.getGeometry().getCoordinates();
            const originalCoordinate = feature.get('originalGeometry').getCoordinates();
            if (!movedCoordinate || !originalCoordinate) {
              return;
            }
            return new LineString([movedCoordinate, originalCoordinate]);
          });
          const clonedOriginalStyle = layer.getStyle();
          const twinStyle = StyleUtil.appendStyle(clonedOriginalStyle, calloutStyle);
          // TODO: make a restriction on the resolution
          // TODO: error on zoom out very far
          //      "Uncaught TypeError: Cannot read property 'getImage' of "
          twinLayer.setStyle(twinStyle);

          // remove original select interaction
          // this prevents a interaction of the original cluster point
          const correspondingInteraction = MapInteractionUtil.getSelectInteraction(
            this.map,
            layer.get('lid')
          );
          if (correspondingInteraction) {
            this.map.removeInteraction(correspondingInteraction);
          }

          // selection
          twinLayer.set('selectable', layer.get('selectable'));
          twinLayer.set('selectStyle', layer.get('selectStyle'));
          // if layer is selectable register a select interaction
          const doAppendSelectStyle = layer.get('doAppendSelectStyle');
          if (twinLayer.get('selectable')) {
            const selectClick = MapInteractionUtil.createSelectInteraction(
              twinLayer,
              twinLayer.get('selectStyle'),
              doAppendSelectStyle);
            // register/activate interaction on map
            this.map.addInteraction(selectClick);
          }
          this.map.addLayer(twinLayer);

          // replace original source with cluster source
          const clusterSrc = new ClusterSource({
          // 0 pixel means identical features
            distance: clusterConf.clusterDistance || 0, // pixel
            source: layer.getSource()
          });

          // when cluster source changes
          // we clear the source of the twin layer
          clusterSrc.on('change', (event) => {
            twinSource.clear();
          });

          const clusterCircleRadius = clusterConf.clusterRadius || 40;

          const clusterStyle = (cluster, resolution) => {
            const features = cluster.get('features');
            const count = features.length;

            if (count === 1) {
              const singleFeature = features[0].clone();
              twinSource.addFeature(singleFeature);
              return null;
            } else {
              const centerStyleConfig = clusterConf.centerStyle || {
                radius: 2,
                fillColor: 'black'
              };
              const centerStyle = OlStyleFactory.getInstance(centerStyleConfig);
              const clusterStyle = [centerStyle];

              // compute circle coordinates for each feature
              features.forEach((feature, index) => {
                const clonedFeature = feature.clone();
                const oldGeometry = clonedFeature.getGeometry();
                const oldCoord = oldGeometry.getCoordinates();
                const newCoord = this.getCircleCoordinate(
                  oldCoord,
                  count,
                  index,
                  clusterCircleRadius
                );
                const newGeometry = new Point(newCoord);

                clonedFeature.setGeometry(newGeometry);
                // add original geometry, necessary for callout lines
                clonedFeature.set('originalGeometry', oldGeometry);

                twinSource.addFeature(clonedFeature);
              });
              return clusterStyle;
            }
          };

          // applied modified source and style
          // to original layer
          layer.setSource(clusterSrc);
          layer.setStyle(clusterStyle);
        });
      },

      /**
       * Compute the coordinate of a de-clustered feature.
       *
       * @param {ol.coordinate} coordinate The original coordinate of the feature
       * @param {int} clusterCount The count of features in the current cluster
       * @param {int} index The index of the feature in the cluster
       * @param {int} [radius=40] The radius of the circle in pixel
       *
       * @returns {ol.coordinate} The new coordinate in the map CRS
       */
      getCircleCoordinate (coordinate, clusterCount, index, radius) {
        if (!coordinate || !clusterCount) {
          return;
        }
        if (!index && index !== 0) {
          return;
        }

        // TODO: incease radius based on number of features
        if (!radius) {
          radius = 40;
        }

        const oldPixelCoord = this.map.getPixelFromCoordinate(coordinate);

        const maxAngle = 2 * Math.PI;
        let angle = (maxAngle / clusterCount) * (index);
        // TODO: make rotation configurable
        // rotate by 45°
        angle = angle + Math.PI / 4;

        const yValue = Math.sin(angle) * radius;
        const xValue = Math.cos(angle) * radius;

        oldPixelCoord[0] = oldPixelCoord[0] + xValue;
        oldPixelCoord[1] = oldPixelCoord[1] - yValue;

        return this.map.getCoordinateFromPixel(oldPixelCoord);
      }

    }
  }
</script>



// WEBPACK FOOTER //
// app/WguApp.vue